import { formatNZDate, formatNZDateTime } from "../../utils/timezone";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Card, CardContent } from "../../components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Slider } from "../../components/ui/slider";
import { Edit, Lock, Eye, Trash2, Car, Clock, MapPin, Zap, Package, ChevronRight } from "lucide-react";

// Service type labels
const SERVICE_TYPE_LABELS = {
  tyres_only: "Tyres Only",
  tyres_alignment: "Tyres + Alignment",
  tyres_alignment_rotation: "Tyres + Alignment + Rotation",
  alignment_only: "Wheel Alignment Only",
  rotation_only: "Tyre Rotation Only",
};

// Get service label from request
const getServiceLabel = (request) => {
  if (request.selected_services && request.selected_services.length > 0) {
    const services = request.selected_services;
    if (services.includes("tyres") && services.includes("alignment") && services.includes("rotation")) {
      return "Tyres + Alignment + Rotation";
    } else if (services.includes("tyres") && services.includes("alignment")) {
      return "Tyres + Alignment";
    } else if (services.includes("tyres")) {
      return "Tyres Only";
    } else if (services.includes("alignment")) {
      return "Wheel Alignment Only";
    } else if (services.includes("rotation")) {
      return "Tyre Rotation Only";
    }
    return services.join(", ");
  }
  return SERVICE_TYPE_LABELS[request.service_type] || request.service_type || "Service";
};

// Comparison intent labels (Feb 2026)
const COMPARISON_INTENT_OPTIONS = [
  { value: "specific", label: "I know the tyre brand/model I want" },
  { value: "budget", label: "Best option within my budget" },
  { value: "open", label: "Open to suggestions" },
];

const DRIVING_PRIORITY_OPTIONS = [
  { value: "long_lasting", label: "Long lasting" },
  { value: "quiet_ride", label: "Quiet ride" },
  { value: "performance", label: "Performance" },
  { value: "ev_efficiency", label: "EV efficiency" },
  { value: "wet_grip", label: "Wet weather grip" },
];

// Price range labels
const PRICE_RANGE_LABELS = {
  budget: "Budget (Economy)",
  mid: "Mid-Range",
  premium: "Premium",
  any: "Any Price Range",
};

// Status colors
const STATUS_COLORS = {
  active: "bg-green-500/10 text-green-600 border-green-500/30",
  pending: "bg-yellow-500/10 text-yellow-600 border-yellow-500/30",
  accepted: "bg-blue-500/10 text-blue-600 border-blue-500/30",
  completed: "bg-purple-500/10 text-purple-600 border-purple-500/30",
  closed: "bg-gray-500/10 text-gray-600 border-gray-500/30",
  deleted: "bg-red-500/10 text-red-600 border-red-500/30",
};

// Check if request is editable (no quotes received yet and not expired)
const isEditable = (request) => {
  if (!request) return false;
  if (request.quotes_received > 0) return false;
  if (["accepted", "completed", "closed", "expired"].includes(request.request_status)) return false;
  return true;
};

// Get lock reason
const getLockReason = (request) => {
  if (!request) return null;
  if (request.request_status === "expired") return "This request has expired";
  if (request.quotes_received > 0) return "Locked: a shop has submitted a quote";
  if (["accepted", "completed", "closed"].includes(request.request_status)) return `Locked: ${request.request_status}`;
  return null;
};

// Edit time remaining - no longer time-based
const getEditTimeRemaining = () => null;

export function CustomerViewRequestDialog({
  viewRequestDialogOpen,
  setViewRequestDialogOpen,
  viewingRequest,
  setViewingRequest,
  editMode,
  setEditMode,
  editedRequest,
  setEditedRequest,
  savingEdit,
  handleSaveEdit,
  deleteDialogOpen,
  setDeleteDialogOpen,
  deleteReason,
  setDeleteReason,
  deleting,
  handleDeleteRequest,
  handleViewQuotes,
}) {
  return (
    <>
      <Dialog open={viewRequestDialogOpen} onOpenChange={(open) => {
        setViewRequestDialogOpen(open);
        if (!open) {
          setEditMode(false);
          setViewingRequest(null);
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <span>Request {viewingRequest?.request_number || `#${viewingRequest?.request_id}`}</span>
              {viewingRequest && isEditable(viewingRequest) ? (
                <Badge className="bg-green-500/10 text-green-600 border-green-500/30">
                  <Edit className="h-3 w-3 mr-1" />
                  Editable ({getEditTimeRemaining(viewingRequest)} min left)
                </Badge>
              ) : (
                <Badge className="bg-gray-500/10 text-gray-600 border-gray-500/30">
                  <Lock className="h-3 w-3 mr-1" />
                  Locked
                </Badge>
              )}
            </DialogTitle>
            <DialogDescription>
              {viewingRequest && isEditable(viewingRequest) 
                ? "You can edit this request for the next " + getEditTimeRemaining(viewingRequest) + " minutes"
                : (getLockReason(viewingRequest) || "This request can no longer be edited")
              }
            </DialogDescription>
          </DialogHeader>

          {viewingRequest && (
            <div className="space-y-6 py-4">
              {/* Status & Type Info */}
              <div className="flex flex-wrap gap-2">
                <Badge className={STATUS_COLORS[viewingRequest.request_status] || STATUS_COLORS.active}>
                  {viewingRequest.request_status}
                </Badge>
                {viewingRequest.request_type === "urgent" && (
                  <Badge className="bg-primary/10 text-primary border-primary/30">
                    <Zap className="h-3 w-3 mr-1" />
                    Urgent
                  </Badge>
                )}
              </div>

              {/* Non-editable fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card className="bg-muted/30">
                  <CardContent className="p-4">
                    <Label className="text-muted-foreground text-xs">Service Type</Label>
                    <p className="font-medium mt-1 flex items-center gap-2">
                      <Package className="h-4 w-4 text-primary" />
                      {getServiceLabel(viewingRequest)}
                    </p>
                  </CardContent>
                </Card>

                <Card className="bg-muted/30">
                  <CardContent className="p-4">
                    <Label className="text-muted-foreground text-xs">Vehicle</Label>
                    <p className="font-medium mt-1 flex items-center gap-2">
                      <Car className="h-4 w-4 text-primary" />
                      {viewingRequest.vehicle_info?.rego || viewingRequest.rego || 
                       `${viewingRequest.vehicle_info?.make || ''} ${viewingRequest.vehicle_info?.model || ''} ${viewingRequest.vehicle_info?.year || ''}`.trim() || 
                       "Not specified"}
                    </p>
                  </CardContent>
                </Card>

                <Card className="bg-muted/30">
                  <CardContent className="p-4">
                    <Label className="text-muted-foreground text-xs">Location</Label>
                    <p className="font-medium mt-1 flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-primary" />
                      {typeof viewingRequest.address === 'object' 
                        ? viewingRequest.address?.full_address 
                        : viewingRequest.address || viewingRequest.postcode || "Not specified"}
                    </p>
                  </CardContent>
                </Card>

                <Card className="bg-muted/30">
                  <CardContent className="p-4">
                    <Label className="text-muted-foreground text-xs">Created</Label>
                    <p className="font-medium mt-1 flex items-center gap-2">
                      <Clock className="h-4 w-4 text-primary" />
                      {formatNZDateTime(viewingRequest.created_at)}
                    </p>
                  </CardContent>
                </Card>

                {viewingRequest.tyre_specs && (viewingRequest.tyre_specs.width || viewingRequest.tyre_specs.aspect_ratio || viewingRequest.tyre_specs.rim_size) && (
                  <Card className="bg-muted/30 md:col-span-2">
                    <CardContent className="p-4">
                      <Label className="text-muted-foreground text-xs">Tyre Specifications</Label>
                      <p className="font-medium mt-1">
                        {viewingRequest.tyre_specs.width}/{viewingRequest.tyre_specs.aspect_ratio}R{viewingRequest.tyre_specs.rim_size}
                      </p>
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* Editable fields */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-semibold">Preferences</h4>
                  {viewingRequest && isEditable(viewingRequest) && !editMode && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setEditMode(true)}
                      data-testid="enable-edit-btn"
                    >
                      <Edit className="h-4 w-4 mr-2" />
                      Edit Preferences
                    </Button>
                  )}
                </div>

                {editMode ? (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Search Radius</Label>
                      <div className="flex items-center gap-4">
                        <Slider
                          value={[editedRequest?.search_radius || 30]}
                          onValueChange={([value]) => setEditedRequest(prev => ({ ...prev, search_radius: value }))}
                          min={5}
                          max={100}
                          step={5}
                          className="flex-1"
                        />
                        <span className="font-medium w-16 text-right">{editedRequest?.search_radius} km</span>
                      </div>
                    </div>

                    {(viewingRequest.service_type?.includes("tyres") || viewingRequest.selected_services?.includes("tyres")) && (
                      <>
                        <div className="space-y-2">
                          <Label>Quantity</Label>
                          <Select
                            value={editedRequest?.quantity?.toString()}
                            onValueChange={(value) => setEditedRequest(prev => ({ ...prev, quantity: parseInt(value) }))}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="1">1 Tyre</SelectItem>
                              <SelectItem value="2">2 Tyres</SelectItem>
                              <SelectItem value="4">4 Tyres (Full Set)</SelectItem>
                              <SelectItem value="5">5 Tyres (Inc. Spare)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-2">
                          <Label>Looking For</Label>
                          <Select
                            value={editedRequest?.comparison_intent || ""}
                            onValueChange={(value) => setEditedRequest(prev => ({ ...prev, comparison_intent: value }))}
                          >
                            <SelectTrigger data-testid="edit-comparison-intent">
                              <SelectValue placeholder="Select option" />
                            </SelectTrigger>
                            <SelectContent>
                              {COMPARISON_INTENT_OPTIONS.map((o) => (
                                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        {editedRequest?.comparison_intent === "specific" && (
                          <>
                            <div className="space-y-2">
                              <Label>Tyre Brand</Label>
                              <Input
                                data-testid="edit-tyre-brand"
                                value={editedRequest?.tyre_brand || ""}
                                onChange={(e) => setEditedRequest(prev => ({ ...prev, tyre_brand: e.target.value }))}
                                placeholder="e.g. Michelin, Bridgestone"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>Tyre Model</Label>
                              <Input
                                data-testid="edit-tyre-model"
                                value={editedRequest?.tyre_model || ""}
                                onChange={(e) => setEditedRequest(prev => ({ ...prev, tyre_model: e.target.value }))}
                                placeholder="e.g. Pilot Sport 5, Turanza T005"
                              />
                            </div>
                          </>
                        )}

                        {editedRequest?.comparison_intent === "budget" && (
                          <div className="space-y-2">
                            <Label>Driving Priority</Label>
                            <Select
                              value={editedRequest?.driving_priority || ""}
                              onValueChange={(value) => setEditedRequest(prev => ({ ...prev, driving_priority: value }))}
                            >
                              <SelectTrigger data-testid="edit-driving-priority">
                                <SelectValue placeholder="Pick what matters most" />
                              </SelectTrigger>
                              <SelectContent>
                                {DRIVING_PRIORITY_OPTIONS.map((o) => (
                                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        )}

                        <div className="space-y-2">
                          <Label>Price Range</Label>
                          <Select
                            value={editedRequest?.price_range}
                            onValueChange={(value) => setEditedRequest(prev => ({ ...prev, price_range: value }))}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {Object.entries(PRICE_RANGE_LABELS).map(([value, label]) => (
                                <SelectItem key={value} value={value}>{label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-2">
                          <Label>Preferred Brand/Model (Optional)</Label>
                          <Input
                            value={editedRequest?.brand_preference || ""}
                            onChange={(e) => setEditedRequest(prev => ({ ...prev, brand_preference: e.target.value }))}
                            placeholder="e.g., Michelin Pilot Sport, Bridgestone Potenza..."
                          />
                        </div>
                      </>
                    )}

                    {/* Rego - editable */}
                    <div className="space-y-2">
                      <Label>Vehicle Rego</Label>
                      <Input
                        data-testid="edit-rego"
                        value={editedRequest?.vehicle_info?.rego || ""}
                        onChange={(e) => setEditedRequest(prev => ({ ...prev, vehicle_info: { ...prev.vehicle_info, rego: e.target.value.toUpperCase() } }))}
                        placeholder="e.g., ABC123"
                      />
                    </div>

                    {/* Tyre Specs - editable when tyres selected */}
                    {viewingRequest.selected_services?.includes("tyres") && (
                      <div className="grid grid-cols-3 gap-3">
                        <div className="space-y-2">
                          <Label>Width</Label>
                          <Input
                            data-testid="edit-tyre-width"
                            value={editedRequest?.tyre_specs?.width || ""}
                            onChange={(e) => setEditedRequest(prev => ({ ...prev, tyre_specs: { ...prev.tyre_specs, width: e.target.value } }))}
                            placeholder="e.g., 205"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Profile</Label>
                          <Input
                            data-testid="edit-tyre-profile"
                            value={editedRequest?.tyre_specs?.aspect_ratio || ""}
                            onChange={(e) => setEditedRequest(prev => ({ ...prev, tyre_specs: { ...prev.tyre_specs, aspect_ratio: e.target.value } }))}
                            placeholder="e.g., 55"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Rim Size</Label>
                          <Input
                            data-testid="edit-tyre-rim"
                            value={editedRequest?.tyre_specs?.rim_size || ""}
                            onChange={(e) => setEditedRequest(prev => ({ ...prev, tyre_specs: { ...prev.tyre_specs, rim_size: e.target.value } }))}
                            placeholder="e.g., 16"
                          />
                        </div>
                      </div>
                    )}

                    {/* Customer Note - always editable */}
                    <div className="space-y-2">
                      <Label>Note to Shops (Optional)</Label>
                      <Textarea
                        value={editedRequest?.customer_note || ""}
                        onChange={(e) => setEditedRequest(prev => ({ ...prev, customer_note: e.target.value }))}
                        placeholder="Any special requirements or questions for the shops..."
                        rows={3}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <Label className="text-muted-foreground text-xs">Search Radius</Label>
                      <p className="font-medium">{viewingRequest.search_radius} km</p>
                    </div>
                    {(viewingRequest.service_type?.includes("tyres") || viewingRequest.selected_services?.includes("tyres")) && (
                      <>
                        <div className="space-y-1">
                          <Label className="text-muted-foreground text-xs">Quantity</Label>
                          <p className="font-medium">{viewingRequest.quantity} tyres</p>
                        </div>
                        {viewingRequest.comparison_intent && (
                          <div className="space-y-1">
                            <Label className="text-muted-foreground text-xs">Looking For</Label>
                            <p className="font-medium">
                              {COMPARISON_INTENT_OPTIONS.find(o => o.value === viewingRequest.comparison_intent)?.label || viewingRequest.comparison_intent}
                            </p>
                          </div>
                        )}
                        {viewingRequest.comparison_intent === "specific" && (viewingRequest.tyre_brand || viewingRequest.tyre_model) && (
                          <div className="space-y-1 col-span-2">
                            <Label className="text-muted-foreground text-xs">Requested Tyre</Label>
                            <p className="font-medium">{[viewingRequest.tyre_brand, viewingRequest.tyre_model].filter(Boolean).join(" ")}</p>
                          </div>
                        )}
                        {viewingRequest.driving_priority && (
                          <div className="space-y-1">
                            <Label className="text-muted-foreground text-xs">Driving Priority</Label>
                            <p className="font-medium">
                              {DRIVING_PRIORITY_OPTIONS.find(o => o.value === viewingRequest.driving_priority)?.label || viewingRequest.driving_priority}
                            </p>
                          </div>
                        )}
                        <div className="space-y-1">
                          <Label className="text-muted-foreground text-xs">Price Range</Label>
                          <p className="font-medium">{PRICE_RANGE_LABELS[viewingRequest.price_range] || viewingRequest.price_range || "Not specified"}</p>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-muted-foreground text-xs">Preferred Brand/Model</Label>
                          <p className="font-medium">{viewingRequest.brand_preference || "Any brand"}</p>
                        </div>
                      </>
                    )}
                    {/* Customer Note - always shown if exists */}
                    {viewingRequest.customer_note && (
                      <div className="space-y-1 col-span-2">
                        <Label className="text-muted-foreground text-xs">Note to Shops</Label>
                        <p className="font-medium">{viewingRequest.customer_note}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Quotes Summary */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-semibold">Quotes Received</h4>
                    <p className="text-sm text-muted-foreground">
                      {viewingRequest.quotes_received > 0 
                        ? `${viewingRequest.quotes_received} quotes from local shops`
                        : "Waiting for shops to respond..."
                      }
                    </p>
                  </div>
                  {viewingRequest.quotes_received > 0 && handleViewQuotes && (
                    <Button
                      onClick={() => {
                        setViewRequestDialogOpen(false);
                        handleViewQuotes(viewingRequest);
                      }}
                    >
                      View Quotes
                      <ChevronRight className="h-4 w-4 ml-2" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            {editMode ? (
              <div className="flex gap-2 w-full justify-end">
                <Button variant="outline" onClick={() => setEditMode(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveEdit}
                  disabled={savingEdit}
                  className="bg-primary"
                >
                  {savingEdit ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            ) : (
              <Button variant="outline" onClick={() => setViewRequestDialogOpen(false)}>
                Close
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Request Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <Trash2 className="h-5 w-5" />
              Delete Quote Request
            </DialogTitle>
            <DialogDescription>
              All information regarding this request and any quotes received will be deleted.
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="delete-reason">Reason for deletion (required)</Label>
              <Textarea
                id="delete-reason"
                placeholder="Please tell us why you're deleting this request..."
                value={deleteReason}
                onChange={(e) => setDeleteReason(e.target.value)}
                rows={3}
                data-testid="delete-reason-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setDeleteDialogOpen(false);
                setDeleteReason("");
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteRequest}
              disabled={deleting || !deleteReason.trim()}
              data-testid="confirm-delete-btn"
            >
              {deleting ? "Deleting..." : "Delete Request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
